/**
 * Where the user was looking in a grid, remembered per browser like Google Sheets does.
 *
 * The anchor is the first visible row and column, by id AND by position: the id finds the
 * same cell after rows are inserted above it, and the position finds the nearest surviving
 * neighbour when that row or column was deleted or sent to trash.
 */
export interface GridViewportAnchor {
  readonly rowKey: string | null;
  readonly rowIndex: number;
  readonly colKey: string | null;
  readonly colIndex: number;
  /** Scrolled to the very end: restore to the end even if the window is now a different height. */
  readonly atBottom?: boolean;
}

const storageKey = (gridKey: string) => `numtable_viewport_${gridKey}`;

export function readGridViewport(gridKey: string): GridViewportAnchor | null {
  try {
    const raw = localStorage.getItem(storageKey(gridKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GridViewportAnchor>;
    if (typeof parsed.rowIndex !== 'number' || typeof parsed.colIndex !== 'number') return null;
    return {
      rowKey: typeof parsed.rowKey === 'string' ? parsed.rowKey : null,
      rowIndex: parsed.rowIndex,
      colKey: typeof parsed.colKey === 'string' ? parsed.colKey : null,
      colIndex: parsed.colIndex,
      atBottom: parsed.atBottom === true,
    };
  } catch {
    return null;
  }
}

export function saveGridViewport(gridKey: string, anchor: GridViewportAnchor): void {
  try {
    localStorage.setItem(storageKey(gridKey), JSON.stringify(anchor));
  } catch {
    // Storage full or blocked: losing the scroll position is harmless.
  }
}

/** The index to scroll to: the anchored item if it still exists, else the nearest survivor. */
export function resolveAnchorIndex(keys: readonly string[], key: string | null, index: number): number {
  if (keys.length === 0) return -1;
  if (key !== null) {
    const found = keys.indexOf(key);
    if (found >= 0) return found;
  }
  return Math.min(Math.max(0, index), keys.length - 1);
}

/** The first item whose far edge is past `offset`, given each item's size in order. */
export function firstVisibleIndex(sizes: readonly number[], offset: number): number {
  let edge = 0;
  for (let i = 0; i < sizes.length; i++) {
    edge += sizes[i]!;
    if (edge > offset + 1) return i;
  }
  return Math.max(0, sizes.length - 1);
}

/** Sum of the sizes before `index`. */
export function offsetOf(sizes: readonly number[], index: number): number {
  let total = 0;
  for (let i = 0; i < index && i < sizes.length; i++) total += sizes[i]!;
  return total;
}
